import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import { createWarehouse, resetCreateStatus, resetUpdateStatus, selectCreateWarehouseError, selectCreateWarehouseLoading, selectSelectedWarehouse, selectUpdateWarehouseError, selectUpdateWarehouseLoading, updateWarehouse } from "../../redux/warehouse/warehouseSlice";
import { closeModal, setHasToRefresh, setGoingToClose } from "../../redux/rightModalSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import LoadingSection from "../LoadingSection";

export default function AddEditWarehouse({ mode }) {
    const dispatch = useDispatch();
    const isEditMode = mode === 'edit';
    const selectedWarehouse = useSelector(selectSelectedWarehouse);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');

    const createLoading = useSelector(selectCreateWarehouseLoading);
    const createError = useSelector(selectCreateWarehouseError);
    const updateLoading = useSelector(selectUpdateWarehouseLoading);
    const updateError = useSelector(selectUpdateWarehouseError);

    const [formData, setFormData] = useState({
        name: '',
        address: '',
        postalCode: '',
        city: '',
        province: '',
        positions: []
    });

    const [newPosition, setNewPosition] = useState({ name: '', code: '' });
    const [validation, setValidation] = useState({ name: true });

    useEffect(() => {
        if (isEditMode && selectedWarehouse) {
            setFormData({
                name: selectedWarehouse.name || '',
                address: selectedWarehouse.address || '',
                postalCode: selectedWarehouse.postalCode || '',
                city: selectedWarehouse.city || '',
                province: selectedWarehouse.province || '',
                positions: selectedWarehouse.positions || []
            });
        }
    }, [isEditMode, selectedWarehouse]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name in validation) setValidation(prev => ({ ...prev, [name]: true }));
    };

    const handleAddPosition = () => {
        if (!newPosition.name.trim()) return;
        setFormData(prev => ({ ...prev, positions: [...prev.positions, { ...newPosition }] }));
        setNewPosition({ name: '', code: '' });
    };

    const handleRemovePosition = (index) => {
        setFormData(prev => ({ ...prev, positions: prev.positions.filter((_, i) => i !== index) }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const newValidation = { name: !!formData.name.trim() };
        setValidation(newValidation);
        if (!Object.values(newValidation).every(v => v)) return;

        const warehouseData = { ...formData, accountId: accountData._id };

        if (isEditMode && selectedWarehouse) {
            dispatch(updateWarehouse({
                warehouse: { ...warehouseData, _id: selectedWarehouse._id, lastUpdateDate: new Date() },
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(setHasToRefresh());
                    dispatch(setGoingToClose());
                }
            });
        } else {
            dispatch(createWarehouse({
                warehouse: { ...warehouseData, creationDate: new Date() },
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(setHasToRefresh());
                    dispatch(setGoingToClose());
                }
            });
        }
    };

    useEffect(() => {
        return () => {
            dispatch(resetCreateStatus());
            dispatch(resetUpdateStatus());
        };
    }, [dispatch]);

    const inputClass = "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
    const inputErrorClass = "w-full px-3 py-2 text-sm border border-red-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-red-50";
    const hasError = createError || updateError;

    return (
        <div className="p-5">
            {(createLoading || updateLoading) && <LoadingSection isFixed />}

            {/* Header */}
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <DynamicIcon iconClass="fa-solid fa-warehouse text-blue-600" />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-gray-900 leading-tight">
                        {isEditMode ? 'Modifica Magazzino' : 'Nuovo Magazzino'}
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                        {isEditMode ? 'Modifica i dati del magazzino' : 'Inserisci i dati del nuovo magazzino'}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nome */}
                <div>
                    <label htmlFor="name" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                        Nome magazzino *
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={!validation.name ? inputErrorClass : inputClass}
                        placeholder="es. Magazzino Principale"
                    />
                    {!validation.name && (
                        <p className="text-red-500 text-xs mt-1">Campo obbligatorio</p>
                    )}
                </div>

                {/* Indirizzo */}
                <div>
                    <label htmlFor="address" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                        Indirizzo
                    </label>
                    <input
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className={inputClass}
                        placeholder="Via / Piazza"
                    />
                </div>

                {/* Citta' + CAP + Provincia */}
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <label htmlFor="city" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                            Città
                        </label>
                        <input
                            type="text"
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Città"
                        />
                    </div>
                    <div>
                        <label htmlFor="postalCode" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                            CAP
                        </label>
                        <input
                            type="text"
                            id="postalCode"
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="CAP"
                        />
                    </div>
                    <div>
                        <label htmlFor="province" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                            Provincia
                        </label>
                        <input
                            type="text"
                            id="province"
                            name="province"
                            value={formData.province}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Prov."
                        />
                    </div>
                </div>

                {/* Posizioni */}
                <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                        Posizioni ({formData.positions.length})
                    </p>

                    {/* Lista posizioni */}
                    {formData.positions.length > 0 ? (
                        <div className="space-y-1.5 mb-3 max-h-40 overflow-y-auto">
                            {formData.positions.map((position, index) => (
                                <div key={index} className="flex items-center justify-between bg-white rounded-md px-3 py-2 border border-gray-100">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className="w-5 h-5 rounded bg-blue-50 flex items-center justify-center flex-shrink-0">
                                            <DynamicIcon iconClass="fa-solid fa-location-crosshairs text-blue-500 text-xs" />
                                        </div>
                                        <span className="text-sm font-medium text-gray-800 truncate">{position.name}</span>
                                        {position.code && (
                                            <span className="text-xs text-gray-400">({position.code})</span>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemovePosition(index)}
                                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0"
                                    >
                                        <DynamicIcon iconClass="fa-solid fa-xmark text-xs" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-xs text-gray-400 mb-3">Nessuna posizione definita</p>
                    )}

                    {/* Aggiungi posizione */}
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={newPosition.name}
                            onChange={(e) => setNewPosition(prev => ({ ...prev, name: e.target.value }))}
                            className={`${inputClass} flex-1`}
                            placeholder="Nome posizione"
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPosition(); } }}
                        />
                        <input
                            type="text"
                            value={newPosition.code}
                            onChange={(e) => setNewPosition(prev => ({ ...prev, code: e.target.value }))}
                            className={`${inputClass} w-24`}
                            placeholder="Codice"
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPosition(); } }}
                        />
                        <button
                            type="button"
                            onClick={handleAddPosition}
                            disabled={!newPosition.name.trim()}
                            className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0 transition-colors"
                        >
                            <DynamicIcon iconClass="fa-solid fa-plus text-sm" />
                        </button>
                    </div>
                </div>

                {/* Errore */}
                {hasError && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                        <DynamicIcon iconClass="fas fa-exclamation-circle flex-shrink-0" />
                        <span>{createError || updateError}</span>
                    </div>
                )}

                {/* Azioni */}
                <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                    <button
                        type="button"
                        className="secondary"
                        onClick={() => dispatch(closeModal())}
                        disabled={createLoading || updateLoading}
                    >
                        Annulla
                    </button>
                    <button
                        type="submit"
                        className="primary"
                        disabled={createLoading || updateLoading}
                    >
                        {createLoading || updateLoading ? (
                            <>
                                <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                Salvataggio...
                            </>
                        ) : (
                            <>
                                <DynamicIcon iconClass="fas fa-save" />
                                {isEditMode ? 'Salva' : 'Crea'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
